import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Bell, CheckCheck } from "lucide-react";
import { toast } from "sonner";
import { Popover, PopoverContent, PopoverTrigger } from "./ui/popover";
import { api } from "../lib/api";
import { SMILEYS, fmtDate } from "../lib/constants";

const ago = (iso) => {
  const m = Math.round((Date.now() - new Date(iso).getTime()) / 60000);
  if (m < 1) return "just now";
  if (m < 60) return `${m}m ago`;
  if (m < 1440) return `${Math.round(m / 60)}h ago`;
  return `${Math.round(m / 1440)}d ago`;
};

export const NotificationBell = () => {
  const nav = useNavigate();
  const [data, setData] = useState({ items: [], unread: 0 });
  const [open, setOpen] = useState(false);
  const seen = useRef(null);

  const load = useCallback(async () => {
    const r = await api.get("/notifications");
    const ids = new Set(r.data.items.map((i) => i.id));
    if (seen.current) {
      r.data.items.filter((i) => !seen.current.has(i.id) && !i.read).forEach((i) => {
        toast.error(`${i.rating}★ review at ${i.hotel}`, { description: `${i.guest_name} · ${i.category}` });
        if (window.Notification?.permission === "granted") new window.Notification(`Low rating: ${i.rating}★ at ${i.hotel}`, { body: `${i.guest_name} · ${i.category}${i.remarks ? ` — ${i.remarks}` : ""}` });
      });
    }
    seen.current = ids;
    setData(r.data);
  }, []);

  useEffect(() => {
    load().catch(() => {});
    if (window.Notification?.permission === "default") window.Notification.requestPermission();
    const t = setInterval(() => { load().catch(() => {}); }, 20000);
    return () => { clearInterval(t); };
  }, [load]);

  const openItem = async (i) => {
    setOpen(false);
    if (!i.read) await api.post(`/notifications/${i.id}/read`);
    load();
    nav(`/dashboard?open=${i.feedback_id}`);
  };
  const readAll = async () => { await api.post("/notifications/read-all"); load(); };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button data-testid="notification-bell" className="relative h-10 w-10 grid place-items-center rounded-full border border-emerald-900/10 bg-white/80 text-emerald-900 hover:bg-amber-50 transition-colors" title="Low rating alerts">
          <Bell className={`h-4 w-4 ${data.unread ? "text-red-600" : ""}`} />
          {data.unread > 0 && <span data-testid="notification-unread-count" className="absolute -top-1 -right-1 min-w-[20px] h-5 px-1 rounded-full bg-red-600 text-white text-[11px] font-bold grid place-items-center ring-2 ring-white">{data.unread}</span>}
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-[360px] p-0 bg-white/95 backdrop-blur-xl rounded-2xl" data-testid="notification-panel">
        <div className="flex items-center justify-between px-4 py-3 border-b">
          <div>
            <div className="font-display text-base font-extrabold text-emerald-950">Low rating alerts</div>
            <div className="text-[11px] text-slate-500">Reviews below 3 stars</div>
          </div>
          {data.unread > 0 && <button data-testid="notification-read-all" onClick={readAll} className="text-xs font-semibold text-emerald-800 hover:underline flex items-center gap-1"><CheckCheck className="h-3.5 w-3.5" /> Mark all read</button>}
        </div>
        <div className="max-h-[420px] overflow-y-auto">
          {data.items.length === 0 && <div className="px-4 py-8 text-center text-sm text-slate-500">No low ratings. All good!</div>}
          {data.items.map((i) => (
            <button key={i.id} data-testid={`notification-item-${i.id}`} onClick={() => openItem(i)} className={`w-full text-left px-4 py-3 border-b last:border-0 flex gap-3 hover:bg-emerald-50/60 transition-colors ${i.read ? "" : "bg-red-50/50"}`}>
              <span className="text-2xl leading-none">{SMILEYS[i.rating - 1].emoji}</span>
              <span className="min-w-0 flex-1">
                <span className="flex justify-between gap-2"><span className="font-semibold text-sm text-emerald-950 truncate">{i.hotel}</span><span className="text-[11px] text-slate-400 shrink-0">{ago(i.created_at)}</span></span>
                <span className="block text-xs text-slate-600">{i.rating}★ · {i.guest_name} · {i.category} · {fmtDate(i.visit_date)}</span>
                {i.remarks && <span className="block text-xs text-slate-500 truncate">{i.remarks}</span>}
              </span>
              {!i.read && <span className="mt-1 h-2 w-2 rounded-full bg-red-500 shrink-0" />}
            </button>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  );
};
